/**
 * ============================================================================
 * AUTHENTICATION ARCHITECTURE - CRITICAL DOCUMENTATION
 * ============================================================================
 * 
 * MEDD-SIM uses TWO authentication methods based on tenant type:
 * 
 * 1. MEDD MASTER TENANT (sim.medd.com.au)
 *    - Uses WordPress SSO via medd.com.au
 *    - User clicks "Sign In" → Redirects to medd.com.au → Returns with JWT token
 *    - This is the ONLY tenant that uses WordPress SSO
 * 
 * 2. WHITE-LABEL TENANTS (gsk.medd.com.au, boston.medd-sim.com, edwards.medd-sim.com, etc.)
 *    - Uses DIRECT Supabase email/password authentication
 *    - NO WordPress SSO - users login directly with email + password
 *    - Each tenant is isolated with their own branding
 * 
 * The `isMasterTenant` flag from TenantContext determines which flow to show.
 * 
 * ⚠️  DO NOT redirect white-label tenants to medd.com.au for authentication!
 * ⚠️  DO NOT show WordPress SSO button for non-master tenants!
 * 
 * See: docs/AUTHENTICATION.md for full documentation
 * ============================================================================
 */

import { useState } from 'react';
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenant, TenantLogo, PoweredBy } from '@/contexts/TenantContext';
import { supabase } from '@/integrations/supabase/client';
import { hasActiveTenantMembership } from '@/lib/tenantMembershipAccess';
import meddLogo from '@/assets/medd-logo.png';

// ============================================
// EMAIL SCOPING FOR TENANT ISOLATION
// ============================================
/**
 * Scope an email to a specific tenant for authentication.
 * Example: scopeEmail("user@gmail.com", "medtronic") => "user+medtronic@gmail.com"
 * This allows the same email to be different users in different tenants.
 */
function scopeEmail(email: string, tenantSlug: string): string {
  const [localPart, domain] = email.split('@');
  if (!domain) return email;
  return `${localPart}+${tenantSlug.toLowerCase()}@${domain}`;
}

interface LoginPromptProps {
  error?: string;
}

export function LoginPrompt({ error: initialError }: LoginPromptProps) {
  const { tenant, isMasterTenant, loading: tenantLoading } = useTenant();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(initialError || '');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  
  // 🧪 STAGING DETECTION
  // Staging environments use direct login (no WordPress SSO) even for master tenant
  // This allows testing master features without SSO dependency
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isStaging = hostname.includes('staging') || 
                    hostname.includes('vercel.app') || 
                    hostname === 'localhost';
  
  // Use WordPress SSO only for production master tenant
  const useWordPressSSO = isMasterTenant && !isStaging;
  
  // 🔒 CRITICAL: Wait for tenant to load before showing login
  // This prevents showing MEDD login briefly before white-label tenant loads
  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Generate background style based on tenant branding
  const backgroundStyle = tenant.login_background_url 
    ? { backgroundImage: `url(${tenant.login_background_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { backgroundColor: tenant.primary_color };

  // Handle Supabase email/password login for white-label tenants
  const handleSupabaseLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Multi-tenant login: try UNSCOPED email first (one account, multiple tenants),
      // then fall back to SCOPED email (legacy: user+tenantslug@domain.com)
      const cleanEmail = email.trim().toLowerCase();
      const scopedEmail = isMasterTenant ? cleanEmail : scopeEmail(cleanEmail, tenant.tenant_slug);

      // Try unscoped email first (multi-tenant: same account across tenants)
      let { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password,
      });

      // If unscoped fails and we're in a white-label tenant, try scoped email (legacy users)
      if (signInError && !isMasterTenant && scopedEmail !== cleanEmail) {
        const scopedResult = await supabase.auth.signInWithPassword({
          email: scopedEmail,
          password,
        });

        if (!scopedResult.error) {
          data = scopedResult.data;
          signInError = null;
        }
      }

      if (signInError) {
        if (signInError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
        } else if (signInError.message.includes('Email not confirmed')) {
          setError('Please check your email and confirm your account first.');
        } else {
          setError(signInError.message);
        }
        return;
      }

      if (data.user) {
        // Check if user has a profile (by auth_user_id, not by id)
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, tenant_id, display_name, auth_user_id')
          .eq('auth_user_id', data.user.id);

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          setError('Error checking account. Please try again.');
          await supabase.auth.signOut();
          return;
        }

        // Active tenant membership is authoritative. A profile's tenant_id,
        // global role, or tenant_admins row must never resurrect access after
        // an administrator suspends the membership through Remove User.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const supabaseAny = supabase as any;
        const { data: membership, error: membershipError } = await supabaseAny
          .from('tenant_memberships')
          .select('id, status')
          .eq('auth_user_id', data.user.id)
          .eq('tenant_id', tenant.tenant_id)
          .maybeSingle();

        if (membershipError) {
          console.error('Tenant membership lookup failed:', membershipError);
          setError('Unable to verify access to this organisation. Please try again.');
          await supabase.auth.signOut();
          return;
        }

        if (!hasActiveTenantMembership(membership)) {
          if (!profiles?.length) {
            setError('No profile found for this account. Please contact support.');
          } else {
            setError('Your account is not registered with this organisation. Please contact your administrator.');
          }
          await supabase.auth.signOut();
          return;
        }

        // Success! Reload page to trigger AuthContext
        window.location.reload();
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset for white-label tenants
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    
    setError('');
    setIsLoading(true);

    try {
      const cleanEmail = email.trim().toLowerCase();
      const { error: resetError } = await supabase.functions.invoke('tenant-password-reset', {
        body: {
          tenant_id: tenant.tenant_id,
          email: cleanEmail,
        },
      });

      if (resetError) {
        setError('Password reset email could not be sent. Please try again or contact support.');
        return;
      }

      setResetEmailSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // For MEDD master tenant, redirect to WordPress SSO
  const handleMEDDLogin = () => {
    window.location.href = `https://medd.com.au/simulation-platform/?redirect_to=${encodeURIComponent(window.location.origin)}`;
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={backgroundStyle}
    >
      {/* Overlay for background images */}
      {tenant.login_background_url && (
        <div className="absolute inset-0 bg-black/40" />
      )}
      
      <div className="max-w-md w-full bg-card rounded-xl shadow-2xl p-8 text-center border border-border relative z-10">
        <div className="mb-6">
          {/* Tenant Logo */}
          <div className="flex justify-center mb-6">
            {isMasterTenant ? (
              // Master tenant: show MEDD logo
              <img 
                src={meddLogo} 
                alt="MEDD Logo" 
                className="h-16 object-contain"
              />
            ) : tenant.logo_url ? (
              // White-label tenant with logo
              <img 
                src={tenant.logo_url} 
                alt={`${tenant.tenant_name} Logo`} 
                className="h-16 object-contain"
                onError={(e) => {
                  // Fallback to text on error
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              // White-label tenant without logo: show tenant name as text
              <h1 
                className="text-4xl font-bold"
                style={{ color: tenant.primary_color }}
              >
                {tenant.tenant_name || 'Welcome'}
              </h1>
            )}
          </div>
          
          {/* Welcome Title */}
          <h1 className="text-2xl font-bold mb-2 text-card-foreground">
            {tenant.login_title || (isMasterTenant ? 'Welcome' : `Welcome to ${tenant.tenant_name}`)}
          </h1>
          
          {/* Subtitle */}
          <p className="text-muted-foreground">
            {tenant.login_subtitle || 'AI-Powered Medical Simulation Excellence'}
          </p>
          
          {/* Authentication message */}
          <p className="text-muted-foreground mt-4 text-sm">
            {isMasterTenant 
              ? 'Please sign in with your MEDD account to continue.'
              : `Please sign in with your ${tenant.tenant_name} account to continue.`
            }
          </p>
          
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive text-left">{error}</p>
            </div>
          )}
        </div>
        
        {/* MEDD Master Tenant: WordPress SSO (production only) */}
        {/* Staging environments use direct login for easier testing */}
        {useWordPressSSO ? (
          <>
            <Button 
              onClick={handleMEDDLogin}
              size="lg"
              className="w-full font-semibold transition-colors"
              style={{
                backgroundColor: tenant.primary_color,
                color: '#FFFFFF',
              }}
            >
              Sign In
            </Button>
            
            <p className="mt-4 text-sm text-muted-foreground">
              After logging in, navigate to "My Tools" and select "Simulation Platform"
            </p>
          </>
        ) : (
          /* White-label Tenant OR Staging Master: Supabase Email/Password Auth */
          <>
            {resetEmailSent ? (
              /* Password Reset Email Sent */
              <div className="space-y-4 text-center">
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-green-800 font-medium">Password reset email sent!</p>
                  <p className="text-green-700 text-sm mt-1">
                    Check your inbox for {email} and follow the link to reset your password.
                  </p>
                  <p className="text-green-600 text-xs mt-2">
                    📧 Don't see it? Check your spam or junk folder.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmailSent(false);
                    setError('');
                  }}
                  className="w-full"
                >
                  Back to Sign In
                </Button>
              </div>
            ) : showForgotPassword ? (
              /* Forgot Password Form */
              <form onSubmit={handleForgotPassword} className="space-y-4 text-left">
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
                
                <Button 
                  type="submit"
                  size="lg"
                  className="w-full font-semibold transition-colors"
                  style={{
                    backgroundColor: tenant.primary_color,
                    color: '#FFFFFF',
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
                
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setError('');
                  }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back to Sign In
                </button>
              </form>
            ) : (
              /* Login Form */
              <form onSubmit={handleSupabaseLogin} className="space-y-4 text-left">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setError('');
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      autoComplete="current-password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <Button 
                  type="submit"
                  size="lg"
                  className="w-full font-semibold transition-colors"
                  style={{
                    backgroundColor: tenant.primary_color,
                    color: '#FFFFFF',
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
                
                <p className="mt-4 text-sm text-muted-foreground text-center">
                  Contact {tenant.support_email || 'support'} if you need help accessing your account.
                </p>
              </form>
            )}
          </>
        )}
        
        {/* Powered By Footer */}
        <div className="mt-6 pt-4 border-t border-border">
          <PoweredBy className="text-xs" />
        </div>
      </div>
    </div>
  );
}
